/**
 * Model catalog (Phase 1). SKELETON from U1a — owned by the integrator (I) after merge.
 *
 * PURE: `import type` + sibling data only. Importable from server/ and src/.
 * Descriptor batches (U4-U8) are registered here by the integrator; until then every
 * registry is empty, so importing this module has no runtime effect on any path.
 */
import type { ModelDescriptor, ModelFamily, Provider, Sku } from './types';

// ── Registered batches (integration-2) ──────────────────────────────────────
// U4 video-a
import { MINIMAX_MODELS } from './fal/minimax';
import { WAN_MODELS } from './fal/wan';
// U5 video-b
import { LTX_MODELS } from './fal/ltx';
import { HUNYUAN_MODELS } from './fal/hunyuan';
import { PIXVERSE_MODELS } from './fal/pixverse';
// U6 video-c
import { KLING_VIDEO_MODELS, KLING_VIDEO_FAMILIES } from './fal/kling-video';
import { SEEDANCE_MODELS } from './fal/seedance';
// (veo-fal — fal's VEO 3.1 — was removed at p1/integration-6: staging retired the bare fal ids
//  veo-3.1 / veo-3.1-fast in e55c20e / migration 141; the VEO node is Google direct.)
// U7 image-a — Z_IMAGE_TURBO is NOT registered yet. Its blockers (R-U7-1, R-U7-2) were cleared
// by U2-MAPPERS (Z_IMAGE_TURBO_BLOCKED = null); registering it is the integrator's step.
import { FLUX_MODELS } from './fal/flux';
import { GLM_IMAGE } from './fal/glm';
// U8 image-b
import { QWEN_MODELS } from './fal/qwen';
import { SEEDREAM_MODELS } from './fal/seedream';
import { RECRAFT_MODELS } from './fal/recraft';
import { KLING_IMAGE_MODELS } from './fal/kling-image';

/** Every registered descriptor. The integrator appends each batch's exports here. */
export const ALL_MODELS: readonly ModelDescriptor[] = [
  ...MINIMAX_MODELS,
  ...WAN_MODELS,
  ...LTX_MODELS,
  ...HUNYUAN_MODELS,
  ...PIXVERSE_MODELS,
  ...KLING_VIDEO_MODELS,
  ...SEEDANCE_MODELS,
  ...FLUX_MODELS,
  GLM_IMAGE,
  ...QWEN_MODELS,
  ...SEEDREAM_MODELS,
  ...RECRAFT_MODELS,
  ...KLING_IMAGE_MODELS,
];

/** Families (H4): one picker entry whose `variant` selects the concrete model. */
export const FAMILIES: readonly ModelFamily[] = [...KLING_VIDEO_FAMILIES];

/**
 * Code-gated set of `${provider}:${skuId}` whose price CreditService.calculateCost and the
 * client quote take from priceSku(). A SKU joins in its family's price-flip PR only, after
 * pricingParity is green against the production dump. EMPTY at U1 merge, and STILL EMPTY at
 * integration-2: registering U4-U8 must remain a customer-visible NO-OP, so no SKU is priced
 * by a descriptor here. See the report's decisionsForOwner for the per-family flip blockers.
 */
export const DESCRIPTOR_PRICED: ReadonlySet<string> = new Set<string>();

export const skuKey = (provider: Provider, skuId: string): string => `${provider}:${skuId}`;

export interface CatalogIndex {
  byId: ReadonlyMap<string, ModelDescriptor>;
  /** key = skuKey(provider, sku.id) */
  bySku: ReadonlyMap<string, { model: ModelDescriptor; sku: Sku }>;
  families: ReadonlyMap<string, ModelFamily>;
}

/**
 * Builds the lookup maps and asserts uniqueness: model ids are unique ACROSS providers,
 * SKU ids are unique per provider, family ids are unique and every family option names a
 * registered model, and every DESCRIPTOR_PRICED key is a registered SKU. Throws on the first
 * violation (runs at module load).
 */
export function buildCatalogIndex(
  models: readonly ModelDescriptor[],
  families: readonly ModelFamily[],
  priced: ReadonlySet<string> = new Set<string>(),
): CatalogIndex {
  const byId = new Map<string, ModelDescriptor>();
  const bySku = new Map<string, { model: ModelDescriptor; sku: Sku }>();
  const fams = new Map<string, ModelFamily>();
  for (const model of models) {
    const prev = byId.get(model.id);
    if (prev) throw new Error(`catalog: duplicate model id '${model.id}' (${prev.provider}, ${model.provider})`);
    byId.set(model.id, model);
    for (const sku of model.pricing.skus) {
      const key = skuKey(model.provider, sku.id);
      const owner = bySku.get(key);
      if (owner) throw new Error(`catalog: duplicate sku '${key}' (models '${owner.model.id}', '${model.id}')`);
      bySku.set(key, { model, sku });
    }
  }
  for (const family of families) {
    if (fams.has(family.id)) throw new Error(`catalog: duplicate family id '${family.id}'`);
    for (const option of family.variant.options) {
      if (!byId.has(option.model)) {
        throw new Error(`catalog: family '${family.id}' option '${option.value}' names unregistered model '${option.model}'`);
      }
    }
    fams.set(family.id, family);
  }
  for (const key of priced) {
    if (!bySku.has(key)) throw new Error(`catalog: DESCRIPTOR_PRICED has '${key}', which is not a registered sku`);
  }
  return { byId, bySku, families: fams };
}

const INDEX = buildCatalogIndex(ALL_MODELS, FAMILIES, DESCRIPTOR_PRICED);

export function byId(id: string): ModelDescriptor | undefined {
  return INDEX.byId.get(id);
}

export function has(id: string): boolean {
  return INDEX.byId.has(id);
}

export function bySku(provider: Provider, skuId: string): { model: ModelDescriptor; sku: Sku } | undefined {
  return INDEX.bySku.get(skuKey(provider, skuId));
}
